import type { FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Account,
  Event,
  IRedirectDomain,
  RedirectDomain,
  RedirectDomains,
} from '@feathr/blackbox';
import { submitModelWithErrorHandling, toast } from '@feathr/components';
import { moment, momentToDate, TimeFormat } from '@feathr/hooks';
import type { ListResponse, TValidateGrouped } from '@feathr/rachis';

export interface IAdvertiserOption {
  id: string;
  name: string;
}

interface IUseProjectSettingsFieldsArgs {
  /** The project's account. Absent while it loads. */
  account?: Account;
  event: Event;
  /** Runs after a clean save, before the success toast. */
  onSaved?: () => void;
  redirectDomains: RedirectDomains;
}

/** State and handlers for the project settings fields. */
export interface IProjectSettingsFields {
  advOptions: IAdvertiserOption[];
  endDate?: Date;
  errors: TValidateGrouped;
  handleChangeMktgPageRdr: (domain: IRedirectDomain) => void;
  handleChangeRefPageRdr: (domain: IRedirectDomain) => void;
  handleChangeTags: (newValue: string[]) => void;
  handleEndDateInput: (rawEvent: FocusEvent<HTMLInputElement>) => void;
  handleSave: () => Promise<void>;
  handleSelectAdvertiser: (option: IAdvertiserOption) => void;
  isAgency: boolean;
  mktPageRdr?: RedirectDomain;
  redirectDomains: ListResponse<RedirectDomain>;
  refPageRdr?: RedirectDomain;
  startDate?: Date;
}

/**
 * Parses a manually-typed date (localized or ISO) to an ISO timestamp, or undefined if incomplete.
 * The end date picker routes onChangeRaw here because react-datepicker drops out-of-range typed dates
 * before firing onChange, so they'd otherwise never reach the model.
 */
export function parseTypedDate(rawValue: string | undefined): string | undefined {
  // Day-clicks also fire onChangeRaw but carry no input value; skip them.
  if (!rawValue) {
    return undefined;
  }
  const parsed = moment(rawValue.trim(), [TimeFormat.shortDate, TimeFormat.isoDate], true);
  return parsed.isValid() ? parsed.startOf('day').format(TimeFormat.isoDateTime) : undefined;
}

/**
 * The project settings fields' state and handlers, without account-context hooks: the caller
 * passes the account and redirect domains, so Perch can supply another account's.
 */
export function useProjectSettingsFields({
  account,
  event,
  onSaved,
  redirectDomains: redirectDomainsCollection,
}: IUseProjectSettingsFieldsArgs): IProjectSettingsFields {
  const { t } = useTranslation();

  const advOptions: IAdvertiserOption[] = (account?.get('ttd')?.subadvertisers || []).map(
    (subadv) => ({
      id: subadv.subadv_id,
      name: subadv.name || '',
    }),
  );

  const refPageRdrDomain = event.get('ref_page_rdr_domain');
  const mktgPageRdrDomain = event.get('mktg_page_rdr_domain');

  const redirectDomains = redirectDomainsCollection.list();
  const refPageRdr = redirectDomains.models.find(
    (d) => refPageRdrDomain && d.id === refPageRdrDomain.id,
  );
  const mktPageRdr = redirectDomains.models.find(
    (d) => mktgPageRdrDomain && d.id === mktgPageRdrDomain.id,
  );

  function handleChangeTags(newValue: string[]): void {
    event.set({ tag_ids: newValue });
  }

  function handleChangeRefPageRdr(domain: IRedirectDomain): void {
    event.set({ ref_page_rdr_domain: domain });
  }

  function handleChangeMktgPageRdr(domain: IRedirectDomain): void {
    event.set({ mktg_page_rdr_domain: domain });
  }

  function handleSelectAdvertiser(option: IAdvertiserOption): void {
    event.set({ subadvertiser: option.id });
  }

  function handleEndDateInput(rawEvent: FocusEvent<HTMLInputElement>): void {
    const parsed = parseTypedDate(rawEvent.currentTarget.value);
    if (parsed) {
      event.set({ date_end: parsed });
    }
  }

  async function handleSave(): Promise<void> {
    // submitModelWithErrorHandling treats an isErrored result (or a rejection) as failure: onSuccess
    // fires only on a clean save, and backend errors surface as an error toast.
    await submitModelWithErrorHandling({
      operation: event.patchDirty(),
      onSuccess: (): void => {
        onSaved?.();
        toast(t('Project changes saved.'), { type: 'success' });
      },
      t,
    });
  }

  const errors = event.validate(['name', 'date_start', 'date_end'], false, 'grouped').errors;

  // Date objects for react-datepicker range linking, so neither picker can select past the other.
  const startStr = event.get('date_start');
  const endStr = event.get('date_end');
  const startDate = startStr ? momentToDate(moment.utc(startStr)) : undefined;
  const endDate = endStr ? momentToDate(moment.utc(endStr)) : undefined;

  return {
    advOptions,
    endDate,
    errors,
    handleChangeMktgPageRdr,
    handleChangeRefPageRdr,
    handleChangeTags,
    handleEndDateInput,
    handleSave,
    handleSelectAdvertiser,
    isAgency: !!account?.get('is_agency'),
    mktPageRdr,
    redirectDomains,
    refPageRdr,
    startDate,
  };
}
